/**
 * Folding the action log: which entries are undone (directly, via a redo
 * chain, or via a `{ fromSeq }` dependency), which rebase is effective for
 * each content entry, and which undo / rebase entries are invalid. Pure;
 * shared by the replay (`log.ts`) and the change groups (`groups.ts`).
 */
const CONTENT_OPS = new Set([
    'replaceText',
    'removeNode',
    'insertParagraph',
    'insertClause',
    'moveSection',
    'replaceRow',
    'removeRow',
    'insertRow',
]);
const FIELD_OPS = new Set(['defineField', 'updateField', 'removeField']);
/** A document edit (replayed as a patch). */
export const isContentEntry = (e) => CONTENT_OPS.has(e.op);
/** An own-field definition change (`defineField` / `updateField` / `removeField`). */
export const isFieldEntry = (e) => FIELD_OPS.has(e.op);
/** An `acceptSuggestion` / `rejectSuggestion` entry. */
export const isSuggestionDecision = (e) => e.op === 'acceptSuggestion' || e.op === 'rejectSuggestion';
/** A content entry submitted as a review suggestion (`proposal: true`). */
export const isProposal = (e) => isContentEntry(e) && e.proposal === true;
/** The anchor / fingerprint an entry replays with, after the latest rebase. */
export function effectiveAnchoring(log, entry) {
    const folded = foldLog(log);
    const rebase = folded.rebases.get(entry.seq);
    return applyRebase(entry, rebase);
}
export function applyRebase(entry, rebase) {
    const to = entry.op === 'moveSection' ? entry.payload.to : undefined;
    const toFingerprint = entry.op === 'moveSection' ? entry.payload.toFingerprint : undefined;
    if (!rebase) {
        return { anchor: entry.anchor, ...(entry.fingerprint ? { fingerprint: entry.fingerprint } : {}), ...(to ? { to } : {}), ...(toFingerprint ? { toFingerprint } : {}) };
    }
    const anchor = rebase.payload.resolution === 'reanchor' && rebase.anchor ? rebase.anchor : entry.anchor;
    const fingerprint = rebase.fingerprint ?? (rebase.payload.resolution === 'reanchor' ? undefined : entry.fingerprint);
    const newTo = rebase.payload.to ?? to;
    const newToFp = rebase.payload.to ? rebase.payload.toFingerprint : (rebase.payload.toFingerprint ?? toFingerprint);
    return {
        anchor,
        ...(fingerprint ? { fingerprint } : {}),
        ...(newTo ? { to: newTo } : {}),
        ...(newToFp ? { toFingerprint: newToFp } : {}),
    };
}
export const dependsOn = (anchor) => (anchor && 'fromSeq' in anchor ? anchor.fromSeq : null);
export function foldLog(log) {
    const entries = [...log.entries].sort((a, b) => a.seq - b.seq);
    const bySeq = new Map();
    const malformed = new Set();
    for (const e of entries) {
        if (!Number.isInteger(e.seq) || e.seq < 1 || bySeq.has(e.seq))
            malformed.add(e.seq);
        else
            bySeq.set(e.seq, e);
    }
    const undone = new Map();
    const invalid = new Set();
    const suggestions = new Map();
    for (const e of entries)
        if (bySeq.get(e.seq) === e && isProposal(e))
            suggestions.set(e.seq, { state: 'open' });
    for (const e of entries) {
        if (bySeq.get(e.seq) !== e || !isSuggestionDecision(e))
            continue;
        const s = suggestions.get(e.payload.targetSeq);
        if (!s || e.payload.targetSeq >= e.seq || s.state !== 'open') {
            invalid.add(e.seq);
            continue;
        }
        suggestions.set(e.payload.targetSeq, { state: e.op === 'acceptSuggestion' ? 'accepted' : 'rejected', decidedBy: e.seq });
    }
    /** A suggestion is an ordinary entry only after its acceptance (`at` = the acting entry's seq). */
    const actsOnApplied = (targetSeq, at) => {
        const s = suggestions.get(targetSeq);
        return !s || (s.state === 'accepted' && s.decidedBy < at);
    };
    for (let i = entries.length - 1; i >= 0; i--) {
        const e = entries[i];
        if (bySeq.get(e.seq) !== e || undone.has(e.seq) || e.op !== 'undo')
            continue;
        const target = bySeq.get(e.payload.targetSeq);
        if (!target || target.seq >= e.seq || isSuggestionDecision(target) || !actsOnApplied(target.seq, e.seq)) {
            invalid.add(e.seq);
            continue;
        }
        if (!undone.has(target.seq))
            undone.set(target.seq, e.seq);
    }
    const rebases = new Map();
    for (const e of entries) {
        if (bySeq.get(e.seq) !== e || undone.has(e.seq) || e.op !== 'rebase')
            continue;
        const target = bySeq.get(e.payload.targetSeq);
        if (!target || target.seq >= e.seq || !isContentEntry(target) || !actsOnApplied(target.seq, e.seq)) {
            invalid.add(e.seq);
            continue;
        }
        rebases.set(target.seq, e);
    }
    for (const e of entries) {
        if (bySeq.get(e.seq) !== e || undone.has(e.seq) || !isContentEntry(e))
            continue;
        const { anchor, to } = applyRebase(e, rebases.get(e.seq));
        for (const dep of [dependsOn(anchor), dependsOn(to)]) {
            if (dep == null)
                continue;
            const by = undone.get(dep);
            if (by != null) {
                undone.set(e.seq, by);
                break;
            }
            const parent = suggestions.get(dep);
            const own = suggestions.get(e.seq);
            if (parent?.state === 'rejected' && own?.state === 'open') {
                suggestions.set(e.seq, { state: 'rejected', ...(parent.decidedBy != null ? { decidedBy: parent.decidedBy } : {}) });
                break;
            }
        }
    }
    return { undone, rebases, invalid, malformed, suggestions };
}
//# sourceMappingURL=fold.js.map